import React from 'react'
import { Box, Alert, Button } from '@mui/material'
import Header from 'components/Header'
import FormField from 'components/FormField'
import CQCLocationSelector from 'components/CQCLocationSelector'
import CQCLocation, { CQC_PREFILLED_FIELDS } from 'logic/CQCLocation'
import ErrorList from 'components/ErrorList'
import { PracticeGroupPracticeForm, WithContactStrings } from 'types/interfaces'
import PracticeGroupPracticeError from 'errors/PracticeGroupPracticeError'
import update from 'immutability-helper'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { usePracticeGroup } from 'contexts/PracticeGroupProvider'
import { useNavigate } from 'react-router'
import { practiceGroupPracticeListURL } from 'routes/urls'
import { useAuthUser } from 'contexts/AuthUserProvider'
import { toContactLists } from 'logic/helpers'
import { MULTIPLE_EMAILS_HELPER_TEXT, MULTIPLE_PHONES_HELPER_TEXT } from 'types/constants'

const PracticeGroupAddPractice: React.FC = () => {
  const { refreshUser } = useAuthUser()
  const { practiceGroup } = usePracticeGroup()
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const navigate = useNavigate()
  const [form, setForm] = React.useState<WithContactStrings<PracticeGroupPracticeForm>>({})
  const [error, setError] = React.useState<PracticeGroupPracticeError>()
  const [loading, setLoading] = React.useState<boolean>(false)
  const [cqcLocation, setCqcLocation] = React.useState<CQCLocation | null>(null)

  const selectCqcLocation = React.useCallback((location: CQCLocation | null) => {
    setCqcLocation(location)
    setForm((form) => update(form, location ? { $merge: location.toPracticeForm() } : { $unset: CQC_PREFILLED_FIELDS }))
    setError((error) => error && update(error, { cqc_location_id: { $set: [] } }))
  }, [])

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(update(form, { [name]: { $set: value } }))
    if (error) setError(update(error, { [name]: { $set: [] } }))
  }, [error, form])

  const save = React.useCallback(async () => {
    if (!practiceGroup) return
    try {
      setLoading(true)
      await api.createPracticeGroupPractice({ practice_group_id: practiceGroup.id, ...toContactLists(form) })
      await refreshUser(true)  // user claims is changed to grant practice_group users access to the new practice.
      showAlert('success', 'New Practice Created')
      navigate(practiceGroupPracticeListURL(practiceGroup.id))
    } catch (e) {
      if (e instanceof PracticeGroupPracticeError) {
        setError(e)
      }
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, form, navigate, practiceGroup, showAlert])
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Header text='Add Practice' />
      <CQCLocationSelector
        label='Enter your practice postcode to find your practice (CQC registered)'
        value={cqcLocation}
        onChange={selectCqcLocation}
        errors={error?.cqc_location_id}
      />
      {error?.cqc_prefilled_errors.length ? (
        <Alert severity='error'><ErrorList errors={error.cqc_prefilled_errors} /></Alert>
      ) : null}
      <FormField
        name='phone_for_inquiries'
        label='Phone Number (General Inquiries)'
        onChange={(e) => updateForm('phone_for_inquiries', e.target.value)}
        value={form.phone_for_inquiries}
        errors={error?.phone_for_inquiries}
        helperText={MULTIPLE_PHONES_HELPER_TEXT}
      />
      <FormField
        name='phone_for_notifications'
        label='Phone Number (to receive SMS notifications)'
        onChange={(e) => updateForm('phone_for_notifications', e.target.value)}
        value={form.phone_for_notifications}
        errors={error?.phone_for_notifications}
        helperText={MULTIPLE_PHONES_HELPER_TEXT}
      />
      <FormField
        name='email_for_inquiries'
        label='Email (General Inquiries)'
        onChange={(e) => updateForm('email_for_inquiries', e.target.value)}
        value={form.email_for_inquiries}
        errors={error?.email_for_inquiries}
        helperText={MULTIPLE_EMAILS_HELPER_TEXT}
      />
      <FormField
        name='email_for_notifications'
        label='Email (to receive notifications)'
        onChange={(e) => updateForm('email_for_notifications', e.target.value)}
        value={form.email_for_notifications}
        errors={error?.email_for_notifications}
        helperText={MULTIPLE_EMAILS_HELPER_TEXT}
      />
      {error?.schema ? (
        <Alert severity='error'><ErrorList errors={error.schema} /></Alert>
      ) : null}
      <Button
        variant="contained"
        color="primary"
        onClick={save}
        disabled={loading}>
        {loading ? 'Please Wait ...' : 'Create Practice'}
      </Button>
    </Box>
  )
}

export default PracticeGroupAddPractice